// Admin query layer. Pairs with `lib/mutations/admin.ts`.
// No 'use client' directive: hooks are React hooks called from client
// components; server pages import the query keys to prefetch.

import {
  infiniteQueryOptions,
  queryOptions,
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
  skipToken,
  type QueryClient,
} from '@tanstack/react-query';
import type { ResourceKind } from '@leanin/shared/resource-kinds';
import type { ResourceDesignedFor } from '@leanin/shared/resource-designed-for';
import type { components, operations } from '@leanin/api-client';
import type { BroadcastSendVia } from '@leanin/shared/broadcast-enums';
import type { CommunityRole } from '@leanin/shared/community-roles';
import { parseReactionState } from '@leanin/shared/reactions';
import type { PageMetaLoaderData } from '../analytics/page-meta';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';
import { editorialTitleSubject } from '../seo-text';
import { loadCurrentForRoute } from './load-for-route';
import { parseResourceSummary } from './resources';

// ---- Re-exported parameter types (mirror views helpers) ----

export type MemberAdminStatus =
  | 'active'
  | 'pending'
  | 'restricted'
  | 'deactivated'
  | 'banned';

export type AdminPlatformRoleFilter = 'admin' | 'moderator' | 'none';
export type AdminCommunityRoleFilter = 'circle_leader' | 'network_leader';
export interface ListAdminMembersInput {
  readonly q?: string;
  readonly status?: MemberAdminStatus;
  readonly platformRole?: AdminPlatformRoleFilter;
  readonly communityRole?: AdminCommunityRoleFilter;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface InvitationsPageParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface GroupInvitesPageParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly groupId?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface GroupInvitesSummaryParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface DsrPageParams {
  readonly status?:
    | 'received'
    | 'approved'
    | 'running'
    | 'completed'
    | 'rejected'
    | 'overdue'
    | 'cancelled';
  readonly kind?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface AdminAssistantOverviewParams {
  readonly mode?: 'default' | 'coach';
}

export interface AdminAssistantConversationsParams {
  readonly mode?: 'default' | 'coach';
  readonly cursor?: string;
  readonly pageSize?: number;
}

// Query-parameter shape of GET /api/v1/admin/moderation/reports, derived
// from the generated OpenAPI types so a server-side change (a new target
// kind, a new filter) surfaces here as a compile error instead of drifting
// silently — the hand-maintained union this replaces was missing 'circle'.
export type AdminReportQueueInput = NonNullable<
  operations['listAdminModerationReports']['parameters']['query']
>;

export type AdminResourceStatus = 'all' | 'draft' | 'published' | 'featured';
export type AdminResourceContentLayer =
  | 'community'
  | 'network'
  | 'public';

export type AdminResourceScope =
  | 'leadership_library'
  | 'circle_leader_tips'
  | 'network_leader_resources';

// `unset` = not yet reviewed (no designed-for value).
export type AdminResourceDesignedFor = ResourceDesignedFor | 'unset';

export interface ListAdminResourcesInput {
  readonly status?: AdminResourceStatus;
  readonly contentLayer?: AdminResourceContentLayer;
  readonly kind?: ResourceKind;
  readonly scope?: AdminResourceScope;
  readonly designedFor?: AdminResourceDesignedFor;
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export type InterviewSegment = CommunityRole | 'any';

export interface InterviewCandidatesInput {
  readonly segment?: InterviewSegment;
  readonly activeDays?: number;
  readonly limit?: number;
  readonly includeContacted?: boolean;
}

export type AudienceSpec = components['schemas']['AudienceSpec'];

export interface PickerInput {
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

// ---- Query keys ----

// Every list leaf keyed by search / filter / cursor params keeps the previous
// page as placeholder data, so a search, tab or page turn swaps the rows in
// place (AdminListRegion dims them while the next page loads) instead of
// dropping back to a loading skeleton on every debounced keystroke.
export const adminKeys = {
  all: ['admin'] as const,

  // Inner-node prefix: every member-scoped cache (list/detail/counts/search).
  members: () => [...adminKeys.all, 'members'] as const,

  membersList: (params: ListAdminMembersInput) =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'list', params] as const,
      queryFn: () => fetchAdminMembersPage(params),
      placeholderData: keepPreviousData,
    }),

  memberDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'detail', id] as const,
      queryFn: () => fetchAdminMemberDetail(id),
      enabled: id.length > 0,
    }),

  memberStatusCounts: () =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'status-counts'] as const,
      queryFn: fetchAdminMemberStatusCounts,
    }),

  memberSearch: (params: PickerInput) =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'search', params] as const,
      queryFn: () => searchAdminMembersForPicker(params),
    }),

  // Inner-node prefix: every user-research cache.
  userResearch: () => [...adminKeys.all, 'user-research'] as const,

  interviewCandidates: (params: InterviewCandidatesInput) =>
    queryOptions({
      queryKey: [...adminKeys.userResearch(), 'candidates', params] as const,
      queryFn: () => fetchAdminInterviewCandidates(params),
      placeholderData: keepPreviousData,
    }),

  invitations: (params: InvitationsPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'invitations', params] as const,
      queryFn: () => fetchAdminInvitations(params),
      placeholderData: keepPreviousData,
    }),

  groupInvites: (params: GroupInvitesPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'group-invites', params] as const,
      queryFn: () => fetchAdminGroupInvites(params),
      placeholderData: keepPreviousData,
    }),

  groupInvitesSummary: (params: GroupInvitesSummaryParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'group-invites-summary', params] as const,
      queryFn: () => fetchAdminGroupInvitesSummary(params),
      placeholderData: keepPreviousData,
    }),

  referralLinks: () =>
    queryOptions({
      queryKey: [...adminKeys.all, 'referral-links'] as const,
      queryFn: fetchReferralLinks,
    }),

  dsr: (params: DsrPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'dsr', 'list', params] as const,
      queryFn: () => fetchAdminDsr(params),
      placeholderData: keepPreviousData,
    }),

  dsrDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'dsr', 'detail', id] as const,
      queryFn: () => fetchAdminDsrDetail(id),
      enabled: id.length > 0,
    }),

  // Inner-node prefix: every broadcast-scoped admin cache.
  broadcasts: () => [...adminKeys.all, 'broadcasts'] as const,

  // `spec` is null while the composer's selection cannot form a valid spec
  // yet (e.g. "Specific members" with nobody picked) — the query idles. The
  // previous count stays on screen while a changed spec refetches.
  broadcastAudience: (spec: AudienceSpec | null, sendVia: BroadcastSendVia) =>
    queryOptions({
      queryKey: [...adminKeys.broadcasts(), 'audience', spec, sendVia] as const,
      queryFn: spec === null ? skipToken : () => fetchAdminBroadcastAudience(spec, sendVia),
      placeholderData: keepPreviousData,
    }),

  broadcastAudienceTargets: (spec: AudienceSpec) =>
    queryOptions({
      queryKey: [...adminKeys.broadcasts(), 'audience-targets', spec] as const,
      queryFn: () => fetchAdminAudienceTargets(spec),
    }),

  // Inner-node prefix: every moderation-scoped admin cache.
  moderation: () => [...adminKeys.all, 'moderation'] as const,

  moderationQueue: (params: AdminReportQueueInput) =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'queue', params] as const,
      queryFn: () => fetchAdminModerationQueue(params),
      placeholderData: keepPreviousData,
    }),

  moderationReport: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'report', id] as const,
      queryFn: () => fetchAdminModerationReport(id),
      enabled: id.length > 0,
    }),

  // The reported DM with up to 4 messages either side — powers the report
  // detail's "View message" preview. A moderator can't open the conversation
  // itself (viewer-scoped route, they're not a member), so this is the only
  // window into the exchange.
  moderationReportMessageContext: (id: string) =>
    queryOptions({
      queryKey: [
        ...adminKeys.moderation(),
        'report',
        id,
        'message-context',
      ] as const,
      queryFn: () => fetchAdminModerationReportMessageContext(id),
      enabled: id.length > 0,
    }),

  // The reported post (or the reported comment's parent post) with a bounded
  // comment window — powers the report detail's "View post" preview. The
  // public /posts/:id route is membership-gated with no admin bypass, so
  // this moderation-scoped window is how a non-member moderator sees the
  // content.
  moderationReportPostContext: (id: string) =>
    queryOptions({
      queryKey: [
        ...adminKeys.moderation(),
        'report',
        id,
        'post-context',
      ] as const,
      queryFn: () => fetchAdminModerationReportPostContext(id),
      enabled: id.length > 0,
    }),

  moderationStats: () =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'stats'] as const,
      queryFn: fetchAdminModerationStats,
    }),

  moderationStatusCounts: () =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'status-counts'] as const,
      queryFn: fetchAdminModerationStatusCounts,
    }),

  // Inner-node prefix: every admin-resource cache.
  resources: () => [...adminKeys.all, 'resources'] as const,

  resourcesList: (params: ListAdminResourcesInput) =>
    queryOptions({
      queryKey: [...adminKeys.resources(), 'list', params] as const,
      queryFn: () => fetchAdminResourcesPage(params),
      placeholderData: keepPreviousData,
    }),

  resourceDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.resources(), 'detail', id] as const,
      queryFn: () => fetchAdminResourceDetail(id),
      enabled: id.length > 0,
    }),

  // The editor must always initialize from the latest server state, never a
  // cached value. Its own always-fresh key (distinct from `resourceDetail`,
  // staleTime/gcTime 0) means a save → reopen cycle re-seeds the form from the
  // server for every field — title, body, cover, attachments — with no
  // per-mutation cache bookkeeping. A cached editor query is what made an
  // admin's saved edit appear not to persist: the editor remounts inside the
  // global 30s staleTime window and re-reads the pre-save value.
  resourceEditor: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.resources(), 'editor', id] as const,
      queryFn: () => fetchAdminResourceDetail(id),
      enabled: id.length > 0,
      staleTime: 0,
      gcTime: 0,
    }),

  circlesPickerInfinite: (params: Omit<PickerInput, 'cursor'> = {}) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.all, 'circles-picker', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminCirclesForPicker({
          ...params,
          cursor: pageParam as string | undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  networksPickerInfinite: (params: Omit<PickerInput, 'cursor'> = {}) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.all, 'networks-picker', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminNetworksForPicker({
          ...params,
          cursor: pageParam as string | undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  // Inner-node prefix: the assistant/coach admin read surface.
  assistant: () => [...adminKeys.all, 'assistant'] as const,

  assistantOverview: (params: AdminAssistantOverviewParams = {}) =>
    queryOptions({
      queryKey: [...adminKeys.assistant(), 'overview', params] as const,
      queryFn: () => fetchAdminAssistantOverview(params),
    }),

  assistantConversationsInfinite: (params: AdminAssistantConversationsParams = {}) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.assistant(), 'conversations', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminAssistantConversations({
          ...params,
          cursor: pageParam as string | undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  assistantTranscript: (conversationId: string) =>
    queryOptions({
      queryKey: [...adminKeys.assistant(), 'transcript', conversationId] as const,
      queryFn: () => fetchAdminAssistantTranscript(conversationId),
      enabled: conversationId.length > 0,
    }),
};

// ---- Client fetchers ----

export async function fetchAdminMembersPage(params: ListAdminMembersInput) {
  const client = getApiClient();
  return unwrap(await client.GET('/api/v1/admin/members', {
    params: { query: params },
  }));
}

export async function fetchAdminMemberDetail(userId: string) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/admin/members/{id}', {
    params: { path: { id: userId } },
  }));
  return data.data;
}

export async function fetchAdminMemberStatusCounts() {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/members/status-counts',
    {},
  ));
  return data;
}

export async function fetchAdminInvitations(params: InvitationsPageParams) {
  const client = getApiClient();
  return unwrap(await client.GET('/api/v1/admin/invitations', {
    params: { query: params },
  }));
}

export async function fetchReferralLinks() {
  const client = getApiClient();
  return unwrap(await client.GET('/api/v1/admin/referral-links'));
}

export async function fetchAdminGroupInvites(params: GroupInvitesPageParams) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/admin/group-invites', {
    params: { query: params },
  }));
  return data;
}

export async function fetchAdminGroupInvitesSummary(
  params: GroupInvitesSummaryParams,
) {
  const client = getApiClient();
  return unwrap(await client.GET(
    '/api/v1/admin/group-invites/summary',
    { params: { query: params } },
  ));
}

export async function fetchAdminDsr(params: DsrPageParams) {
  const client = getApiClient();
  return unwrap(await client.GET(
    '/api/v1/admin/dsr/requests',
    {
      params: {
        query: {
          cursor: params.cursor,
          pageSize: params.pageSize,
          status: params.status,
          kind: params.kind as
            | 'access'
            | 'rectification'
            | 'erasure'
            | 'restriction'
            | 'portability'
            | 'objection'
            | undefined,
        },
      },
    },
  ));
}

export async function fetchAdminDsrDetail(id: string) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/dsr/requests/{id}',
    { params: { path: { id } } },
  ));
  return data;
}

export async function fetchAdminBroadcastAudience(
  spec: AudienceSpec,
  sendVia: BroadcastSendVia,
) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/broadcasts/audience-resolve',
    { params: { query: { spec: JSON.stringify(spec), sendVia } } },
  ));
  return data;
}

export interface AdminAudienceTargets {
  readonly circles: ReadonlyArray<components['schemas']['AdminCirclePickerItem']>;
  readonly networks: ReadonlyArray<components['schemas']['AdminNetworkPickerItem']>;
  readonly members: ReadonlyArray<components['schemas']['AdminMemberPickerItem']>;
}

const NO_AUDIENCE_TARGETS: AdminAudienceTargets = { circles: [], networks: [], members: [] };

// The picker endpoints' `ids` filter takes at most one page (100) of ids.
const PICKER_IDS_PAGE = 100;

function idChunks(ids: readonly string[]): string[] {
  const chunks: string[] = [];
  for (let i = 0; i < ids.length; i += PICKER_IDS_PAGE) {
    chunks.push(ids.slice(i, i + PICKER_IDS_PAGE).join(','));
  }
  return chunks;
}

// Display rows (names, emails) for the ids a stored hand-picked audience
// carries, so the edit form can render the same chips the composer did.
// Ids that no longer resolve (archived group, deactivated member) drop out.
export async function fetchAdminAudienceTargets(
  spec: AudienceSpec,
): Promise<AdminAudienceTargets> {
  const client = getApiClient();
  const query = { pageSize: PICKER_IDS_PAGE };
  switch (spec.kind) {
    case 'circles': {
      const pages = await Promise.all(
        idChunks(spec.circleIds).map(async (ids) => {
          const data = unwrap(await client.GET('/api/v1/admin/circles', {
            params: { query: { ...query, ids } },
          }));
          return data.items;
        }),
      );
      return { ...NO_AUDIENCE_TARGETS, circles: pages.flat() };
    }
    case 'networks': {
      const pages = await Promise.all(
        idChunks(spec.networkIds).map(async (ids) => {
          const data = unwrap(await client.GET('/api/v1/admin/networks', {
            params: { query: { ...query, ids } },
          }));
          return data.items;
        }),
      );
      return { ...NO_AUDIENCE_TARGETS, networks: pages.flat() };
    }
    case 'members': {
      const pages = await Promise.all(
        idChunks(spec.userIds).map(async (ids) => {
          const data = unwrap(await client.GET('/api/v1/admin/members/search', {
            params: { query: { ...query, ids } },
          }));
          return data.items;
        }),
      );
      return { ...NO_AUDIENCE_TARGETS, members: pages.flat() };
    }
    case 'filter':
    case 'spaces':
      return NO_AUDIENCE_TARGETS;
  }
}

export async function fetchAdminModerationQueue(
  params: AdminReportQueueInput,
) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/moderation/reports',
    { params: { query: params } },
  ));
  return data;
}

export async function fetchAdminModerationReport(reportId: string) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/moderation/reports/{id}',
    { params: { path: { id: reportId } } },
  ));
  return data;
}

export async function fetchAdminModerationReportMessageContext(
  reportId: string,
) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/moderation/reports/{id}/message-context',
    { params: { path: { id: reportId } } },
  ));
  return data;
}

export async function fetchAdminModerationReportPostContext(reportId: string) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/moderation/reports/{id}/post-context',
    { params: { path: { id: reportId } } },
  ));
  return data;
}

export async function fetchAdminModerationStats() {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/moderation/stats',
    {},
  ));
  return data;
}

export async function fetchAdminModerationStatusCounts() {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/moderation/status-counts',
    {},
  ));
  return data;
}

export async function fetchAdminResourcesPage(
  params: ListAdminResourcesInput,
) {
  const client = getApiClient();
  const page = unwrap(await client.GET('/api/v1/admin/resources', {
    params: { query: params },
  }));
  return { ...page, items: page.items.map(parseResourceSummary) };
}

export async function fetchAdminResourceDetail(resourceId: string) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/resources/{id}',
    { params: { path: { id: resourceId } } },
  ));
  return parseReactionState(data.data);
}

export async function fetchAdminCirclesForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/admin/circles', {
    params: { query: params },
  }));
  return data;
}

export async function fetchAdminNetworksForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/admin/networks', {
    params: { query: params },
  }));
  return data;
}

export async function searchAdminMembersForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  return unwrap(await client.GET(
    '/api/v1/admin/members/search',
    { params: { query: params } },
  ));
}

export async function fetchAdminInterviewCandidates(
  params: InterviewCandidatesInput,
) {
  const client = getApiClient();
  return unwrap(await client.GET(
    '/api/v1/admin/user-research/candidates',
    {
      params: {
        query: {
          segment: params.segment,
          activeDays: params.activeDays,
          limit: params.limit,
          // Query-string boolean: the server parses 'true'/'false' literals
          // (a bare boolean would arrive as the string "false" and coerce
          // truthy). Omit entirely when false — the server default.
          ...(params.includeContacted ? { includeContacted: 'true' as const } : {}),
        },
      },
    },
  ));
}

// ---- Hooks ----

export function useAdminMembersPage(params: ListAdminMembersInput) {
  return useQuery(adminKeys.membersList(params));
}

export function useAdminMemberDetail(userId: string) {
  return useQuery(adminKeys.memberDetail(userId));
}

export function useAdminInterviewCandidates(params: InterviewCandidatesInput) {
  return useQuery(adminKeys.interviewCandidates(params));
}

export function useAdminMemberStatusCounts() {
  return useQuery(adminKeys.memberStatusCounts());
}

export function useAdminInvitations(params: InvitationsPageParams) {
  return useQuery(adminKeys.invitations(params));
}

export function useReferralLinks() {
  return useQuery(adminKeys.referralLinks());
}

export function useAdminGroupInvites(params: GroupInvitesPageParams) {
  return useQuery(adminKeys.groupInvites(params));
}

export function useAdminGroupInvitesSummary(params: GroupInvitesSummaryParams) {
  return useQuery(adminKeys.groupInvitesSummary(params));
}

export function useAdminDsr(params: DsrPageParams) {
  return useQuery(adminKeys.dsr(params));
}

export function useAdminDsrDetail(id: string) {
  return useQuery(adminKeys.dsrDetail(id));
}

export function useAdminBroadcastAudience(
  spec: AudienceSpec | null,
  sendVia: BroadcastSendVia,
) {
  return useQuery(adminKeys.broadcastAudience(spec, sendVia));
}

export function useAdminBroadcastAudienceTargets(spec: AudienceSpec) {
  return useQuery(adminKeys.broadcastAudienceTargets(spec));
}

export function useAdminModerationQueue(params: AdminReportQueueInput) {
  return useQuery(adminKeys.moderationQueue(params));
}

export function useAdminModerationReport(reportId: string) {
  return useQuery(adminKeys.moderationReport(reportId));
}

export function useAdminModerationReportMessageContext(
  reportId: string,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    ...adminKeys.moderationReportMessageContext(reportId),
    enabled: reportId.length > 0 && (opts.enabled ?? true),
  });
}

export function useAdminModerationReportPostContext(
  reportId: string,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    ...adminKeys.moderationReportPostContext(reportId),
    enabled: reportId.length > 0 && (opts.enabled ?? true),
  });
}

export function useAdminModerationStats() {
  return useQuery(adminKeys.moderationStats());
}

export function useAdminModerationStatusCounts() {
  return useQuery(adminKeys.moderationStatusCounts());
}

export function useAdminResourcesPage(params: ListAdminResourcesInput) {
  return useQuery(adminKeys.resourcesList(params));
}

export function useAdminResourceDetail(
  resourceId: string,
  options?: { retryOnMount?: boolean },
) {
  return useQuery({ ...adminKeys.resourceDetail(resourceId), ...options });
}

// The admin resource page's loader data, read from the entry its view renders.
export async function loadAdminResourceMeta(
  queryClient: QueryClient,
  resourceId: string,
): Promise<PageMetaLoaderData> {
  const resource = await loadCurrentForRoute(queryClient, adminKeys.resourceDetail(resourceId));
  return resource === undefined ? {} : { pageTitle: editorialTitleSubject(resource) };
}

export function useInfiniteCirclesPicker(
  params: Omit<PickerInput, 'cursor'> = {},
) {
  return useInfiniteQuery(adminKeys.circlesPickerInfinite(params));
}

export function useInfiniteNetworksPicker(
  params: Omit<PickerInput, 'cursor'> = {},
) {
  return useInfiniteQuery(adminKeys.networksPickerInfinite(params));
}

// ---- Assistant admin surface ----

export async function fetchAdminAssistantOverview(
  params: AdminAssistantOverviewParams = {},
) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/admin/assistant/overview', {
    params: { query: params },
  }));
  return data;
}

export async function fetchAdminAssistantConversations(
  params: AdminAssistantConversationsParams = {},
) {
  const client = getApiClient();
  return unwrap(await client.GET('/api/v1/admin/assistant/conversations', {
    params: { query: params },
  }));
}

export async function fetchAdminAssistantTranscript(conversationId: string) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/admin/assistant/conversations/{id}',
    { params: { path: { id: conversationId } } },
  ));
  return data;
}

export function useAdminAssistantOverview(params: AdminAssistantOverviewParams = {}) {
  return useQuery(adminKeys.assistantOverview(params));
}

export function useInfiniteAdminAssistantConversations(
  params: Omit<AdminAssistantConversationsParams, 'cursor'> = {},
) {
  return useInfiniteQuery(adminKeys.assistantConversationsInfinite(params));
}

export function useAdminAssistantTranscript(
  conversationId: string,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    ...adminKeys.assistantTranscript(conversationId),
    enabled: conversationId.length > 0 && (opts.enabled ?? true),
  });
}
